import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PackagePlus, ArrowUpRight, TrendingUp } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import DispatchTimeline from "@/components/DispatchTimeline";

function Metric({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="border-2 border-border bg-card p-6 hover:border-orange-600 transition-colors duration-200">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
      <div className={`mt-3 font-mono text-4xl font-black ${accent || ""}`}>{value}</div>
    </div>
  );
}

const STATUS_COLORS = {
  pending: "bg-yellow-500",
  dispatched: "bg-orange-600",
  in_transit: "bg-blue-600",
  completed: "bg-emerald-600",
  cancelled: "bg-zinc-500",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    (async () => {
      const [s, r] = await Promise.all([api.get("/requests/stats"), api.get("/requests")]);
      setStats(s.data);
      setRecent(r.data.slice(0, 8));
    })();
  }, []);

  return (
    <div className="space-y-8">
      <section
        data-testid="welcome-hero"
        className="relative overflow-hidden border-2 border-foreground bg-foreground text-background"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, #FF5A1F 0 2px, transparent 2px 14px)" }}
        />
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 p-6 sm:p-8 md:p-10 items-center">
          <div className="md:col-span-3 flex md:justify-center">
            <div className="p-3 sm:p-4 bg-background/5 border-2 border-orange-500 inline-flex">
              <LogoMark size={72} className="text-white sm:hidden" />
              <LogoMark size={92} className="text-white hidden sm:block" />
            </div>
          </div>
          <div className="md:col-span-6 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.4em] text-orange-500 font-bold">
              Welcome back{user?.name ? `, ${user.name}` : ""}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-[0.95]">
              OUT <span className="text-orange-500">&amp;</span> IN
            </h1>
            <p className="text-sm text-white/70 leading-relaxed max-w-md">
              Every pickup out. Every delivery in. Log a movement and we notify the supplier and the appointed courier — instantly.
            </p>
          </div>
          <div className="md:col-span-3 flex md:justify-end">
            <Link to="/requests/new" className="w-full md:w-auto">
              <Button
                data-testid="welcome-new-request-button"
                className="w-full md:w-auto rounded-none bg-orange-600 hover:bg-orange-700 text-white h-12 px-6 uppercase tracking-[0.2em] text-xs font-bold"
              >
                <PackagePlus className="h-4 w-4 mr-2" /> New Request
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Operations Overview</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">Dashboard</h2>
        </div>
        <Link to="/requests/new">
          <Button data-testid="dashboard-new-request-button" className="rounded-none bg-orange-600 hover:bg-orange-700 h-11 px-6 uppercase tracking-[0.15em] text-xs font-bold">
            <PackagePlus className="h-4 w-4 mr-2" /> New Request
          </Button>
        </Link>
      </div>

      <OnboardingChecklist />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Metric testid="metric-total" label="Total Requests" value={stats?.total ?? "—"} accent="text-foreground" />
        <Metric testid="metric-pickups" label="Pickups" value={stats?.pickups ?? "—"} accent="text-orange-600" />
        <Metric testid="metric-deliveries" label="Deliveries" value={stats?.deliveries ?? "—"} accent="text-orange-600" />
        <Metric testid="metric-pending" label="Pending" value={stats?.pending ?? "—"} accent="text-yellow-600" />
      </div>

      <DispatchTimeline />

      <div className="border-2 border-border bg-card">
        <div className="px-6 py-4 border-b-2 border-border flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-bold">Recent Activity</div>
            <div className="font-heading text-xl font-bold mt-1">Latest requests</div>
          </div>
          <Link to="/requests" className="text-xs uppercase tracking-[0.15em] font-bold text-orange-600 hover:underline flex items-center gap-1">
            View all <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="divide-y-2 divide-border">
          {recent.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No requests yet. Create your first dispatch to see it here.
            </div>
          )}
          {recent.map((r) => (
            <Link
              key={r.request_id}
              to={`/requests/${r.request_id}`}
              data-testid={`recent-row-${r.request_id}`}
              className="flex flex-col sm:grid sm:grid-cols-12 gap-2 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-accent transition-colors duration-200 sm:items-center"
            >
              <div className="sm:col-span-3 font-mono text-sm font-bold">{r.reference_number}</div>
              <div className="sm:col-span-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border">
                  <span className={`h-1.5 w-1.5 ${r.request_type === "pickup" ? "bg-orange-600" : "bg-blue-600"}`} />
                  {r.request_type}
                </span>
              </div>
              <div className="sm:col-span-4 text-sm truncate">{r.pickup?.company} → {r.delivery?.company}</div>
              <div className="sm:col-span-3 sm:text-right">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border">
                  <span className={`h-1.5 w-1.5 ${STATUS_COLORS[r.status] || "bg-zinc-400"}`} />
                  {r.status}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
